(() => {
  'use strict';

  const ROUTE = document.body?.dataset?.r7Route;
  if (ROUTE !== 'collection') return;

  const KEY = '__LDR7CollectionOneStep';
  window[KEY]?.destroy?.();
  const lifecycle = new AbortController();
  const { signal } = lifecycle;

  const media = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const SCENE_SELECTOR = '[data-ld-social-scene], [data-ldx-product-scene]';
  const STORY_SELECTOR = '[data-ldx-product-story], [data-ldx-capelli-products]';
  const IGNORE_SELECTOR = 'input, textarea, select, option, [contenteditable="true"], [role="slider"], [role="spinbutton"], details[open]';
  const THRESHOLD_PX = 24;
  const DURATION_MS = 420;
  const INPUT_IDLE_MS = 500;
  const ACCUMULATOR_RESET_MS = 140;

  let accumulator = 0;
  let accumulatorTimer = 0;
  let locked = false;
  let animationDone = true;
  let lastWheelAt = 0;
  let raf = 0;
  let releaseTimer = 0;

  const visible = (el) => {
    if (!(el instanceof Element)) return false;
    const style = getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden') return false;
    const rect = el.getBoundingClientRect();
    return rect.width > 1 && rect.height > 1;
  };

  const overlaysOpen = () =>
    Boolean(
      document.querySelector('dialog[open], .r7-collection-utility.is-r7-open, .ld-social-commerce-shell.is-commerce-open, .ldx-sakazuki-grid__filters-panel[open], .ldx-capelli-products__filters[open]')
      || document.body.classList.contains('r7-collection-sheet-open')
      || document.body.classList.contains('ld-capelli-details-open')
      || document.body.classList.contains('ld-capelli-ai-open')
    );

  const normalizeDelta = (event) => {
    let delta = event.deltaY;
    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) delta *= 16;
    else if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) delta *= window.innerHeight;
    return delta;
  };

  const docTop = (el) => window.scrollY + el.getBoundingClientRect().top;

  const getStory = () => {
    const roots = Array.from(document.querySelectorAll(STORY_SELECTOR)).filter(visible);
    if (!roots.length) return null;
    const header = Math.max(0, document.getElementById('header-group')?.getBoundingClientRect().height || 0);
    const viewportTop = header;
    const viewportBottom = window.innerHeight;
    const scored = roots.map((root) => {
      const r = root.getBoundingClientRect();
      const overlap = Math.max(0, Math.min(r.bottom, viewportBottom) - Math.max(r.top, viewportTop));
      return { root, overlap };
    }).filter((item) => item.overlap > 0);
    scored.sort((a, b) => b.overlap - a.overlap);
    return scored[0]?.root || null;
  };

  const getScenes = (root) => {
    const raw = Array.from(root.querySelectorAll(SCENE_SELECTOR))
      .filter((scene) => visible(scene) && !scene.hidden);

    const seen = new Set();
    const unique = [];
    raw
      .sort((a, b) => docTop(a) - docTop(b))
      .forEach((scene) => {
        const top = Math.round(docTop(scene));
        if ([...seen].some((value) => Math.abs(value - top) <= 8)) return;
        seen.add(top);
        unique.push(scene);
      });
    return unique;
  };

  const getCurrent = (scenes) => {
    const owner = scenes[0]?.closest('ldx-sakazuki-grid');
    const active = scenes.includes(owner?.activeScene)
      ? owner.activeScene
      : scenes.find((scene) => scene.classList.contains('is-active'));
    if (active) return active;

    const header = Math.max(0, document.getElementById('header-group')?.getBoundingClientRect().height || 0);
    const probe = header + (window.innerHeight - header) * 0.46;
    return scenes.reduce((best, scene) => {
      const r = scene.getBoundingClientRect();
      const center = r.top + Math.min(r.height, window.innerHeight) * 0.5;
      const distance = Math.abs(center - probe);
      return !best || distance < best.distance ? { scene, distance } : best;
    }, null)?.scene || null;
  };

  const getTarget = (scenes, current, direction) => {
    if (!current) return null;
    const currentTop = docTop(current);
    if (direction > 0) {
      return scenes.find((scene) => docTop(scene) > currentTop + 8) || null;
    }
    const previous = scenes.filter((scene) => docTop(scene) < currentTop - 8);
    return previous.length ? previous[previous.length - 1] : null;
  };

  const targetScrollY = (scene) => {
    const header = Math.max(0, document.getElementById('header-group')?.getBoundingClientRect().height || 0);
    const isCapelliChapter = scene.classList.contains('ldx-capelli-chapter');
    const offset = isCapelliChapter ? header + 20 : 0;
    return Math.max(0, Math.round(docTop(scene) - offset));
  };

  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const scheduleRelease = () => {
    clearTimeout(releaseTimer);
    const check = () => {
      const idleFor = performance.now() - lastWheelAt;
      if (animationDone && idleFor >= INPUT_IDLE_MS) {
        locked = false;
        accumulator = 0;
        return;
      }
      releaseTimer = window.setTimeout(check, Math.max(32, INPUT_IDLE_MS - idleFor));
    };
    releaseTimer = window.setTimeout(check, INPUT_IDLE_MS);
  };

  const goTo = (scene) => {
    const end = targetScrollY(scene);
    const start = window.scrollY;
    const delta = end - start;

    locked = true;
    animationDone = false;
    accumulator = 0;

    if (raf) cancelAnimationFrame(raf);

    if (reduced.matches || Math.abs(delta) < 2) {
      window.scrollTo({ left: 0, top: end, behavior: 'instant' });
      animationDone = true;
      scheduleRelease();
      return;
    }

    const started = performance.now();
    const step = (now) => {
      const progress = Math.min(1, (now - started) / DURATION_MS);
      window.scrollTo({ left: 0, top: Math.round(start + delta * easeOutQuart(progress)), behavior: 'instant' });
      if (progress < 1) {
        raf = requestAnimationFrame(step);
        return;
      }
      raf = 0;
      window.scrollTo({ left: 0, top: end, behavior: 'instant' });
      animationDone = true;
      scheduleRelease();
    };
    raf = requestAnimationFrame(step);
  };

  const resetAccumulatorSoon = () => {
    clearTimeout(accumulatorTimer);
    accumulatorTimer = window.setTimeout(() => { accumulator = 0; }, ACCUMULATOR_RESET_MS);
  };

  const onWheel = (event) => {
    if (event.defaultPrevented || !media.matches || event.ctrlKey || overlaysOpen()) {
      if (locked) reset();
      return;
    }
    if (event.target instanceof Element && event.target.closest(IGNORE_SELECTOR)) {
      if (locked) reset();
      return;
    }

    const delta = normalizeDelta(event);
    if (!Number.isFinite(delta) || Math.abs(delta) < 0.5 || Math.abs(event.deltaX) > Math.abs(delta) * 1.2) return;

    lastWheelAt = performance.now();

    if (locked) {
      event.preventDefault();
      scheduleRelease();
      return;
    }

    const root = getStory();
    if (!root) return;

    const scenes = getScenes(root);
    if (scenes.length < 2) return;

    const current = getCurrent(scenes);
    const direction = delta > 0 ? 1 : -1;
    const content = current?.querySelector('.ldx-capelli-chapter__card') || current;
    if (content) {
      const rect = content.getBoundingClientRect();
      const header = Math.max(0, document.getElementById('header-group')?.getBoundingClientRect().height || 0);
      const available = Math.max(1, innerHeight - header - 40);
      if (rect.height > available && ((direction > 0 && rect.bottom > innerHeight - 20) || (direction < 0 && rect.top < header + 20))) {
        accumulator = 0;
        clearTimeout(accumulatorTimer);
        return;
      }
    }
    const target = getTarget(scenes, current, direction);

    if (!target) {
      accumulator = 0;
      return;
    }

    event.preventDefault();

    if (accumulator && Math.sign(accumulator) !== direction) accumulator = 0;
    accumulator += delta;
    resetAccumulatorSoon();

    if (Math.abs(accumulator) < THRESHOLD_PX) return;
    clearTimeout(accumulatorTimer);
    goTo(target);
  };

  const reset = () => {
    accumulator = 0;
    locked = false;
    animationDone = true;
    clearTimeout(accumulatorTimer);
    clearTimeout(releaseTimer);
    if (raf) cancelAnimationFrame(raf);
    raf = 0;
  };

  window.addEventListener('wheel', onWheel, { passive: false, capture: true, signal });
  window.addEventListener('resize', reset, { passive: true, signal });
  window.addEventListener('pagehide', reset, { signal });
  window.addEventListener('pageshow', reset, { signal });
  window.addEventListener('pointerdown', reset, { passive: true, signal });
  window.addEventListener('keydown', reset, { signal });
  media.addEventListener('change', reset, { signal });
  document.addEventListener('shopify:section:load', reset, { signal });
  document.addEventListener('shopify:section:unload', reset, { signal });
  window[KEY] = Object.freeze({ destroy() { lifecycle.abort(); reset(); delete window[KEY]; } });
})();
